import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Paper, CircularProgress, Snackbar, Alert,
  IconButton, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PageHeader from '../components/PageHeader';
import DepartmentHeader from '../components/DepartmentHeader';
import { useDepartment } from '../context/DepartmentContext';
import { getSemesters, createSemester, updateSemester, deleteSemester } from '../services/api';

const Semesters = () => {
  const { selectedDepartment } = useDepartment();
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentSemester, setCurrentSemester] = useState({
    semester_name: '',
    academic_year: '',
    section: '',
    department_id: ''
  });
  const [selectedId, setSelectedId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Update currentSemester when selectedDepartment changes
  useEffect(() => {
    if (selectedDepartment) {
      setCurrentSemester(prev => ({
        ...prev,
        department_id: selectedDepartment.department_id
      }));
    }
  }, [selectedDepartment]);

  const columns = [
    { field: 'semester_id', headerName: 'ID', width: 70 },
    { field: 'semester_name', headerName: 'Semester Name', flex: 1 },
    { field: 'academic_year', headerName: 'Academic Year', width: 150 },
    { 
      field: 'section', 
      headerName: 'Sections', 
      width: 150,
      valueGetter: (params) => {
        if (!params.value) return 'None';
        const sections = params.value.split(',').filter(s => s.trim());
        return sections.length > 0 ? sections.join(', ') : 'None';
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton 
            color="primary" 
            size="small"
            onClick={() => handleOpenDialog(params.row)}
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            color="error" 
            size="small"
            onClick={() => handleDeleteClick(params.row.semester_id)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedDepartment) return;
      
      try {
        setLoading(true);
        const response = await getSemesters();
        
        // Filter semesters by department
        const departmentSemesters = response.data.filter(
          semester => semester.department_id === selectedDepartment.department_id
        );
        
        setSemesters(departmentSemesters);
      } catch (error) {
        console.error('Error fetching semesters:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load semesters',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedDepartment]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDialog = (semester = null) => {
    if (semester) {
      setCurrentSemester({
        semester_name: semester.semester_name,
        academic_year: semester.academic_year,
        section: semester.section || '',
        department_id: selectedDepartment.department_id
      });
      setSelectedId(semester.semester_id);
    } else {
      setCurrentSemester({
        semester_name: '',
        academic_year: new Date().getFullYear().toString(),
        section: '',
        department_id: selectedDepartment.department_id
      });
      setSelectedId(null);
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteSemester(selectedId);
      setSemesters(semesters.filter(semester => semester.semester_id !== selectedId));
      setSnackbar({
        open: true,
        message: 'Semester deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting semester:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete semester',
        severity: 'error'
      });
    } finally {
      setConfirmDelete(false);
      setSelectedId(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSemester(prev => ({ ...prev, [name]: value }));
    
    // Clear the error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!currentSemester.semester_name.trim()) {
      errors.semester_name = 'Semester name is required';
    }
    if (!currentSemester.academic_year.trim()) {
      errors.academic_year = 'Academic year is required';
    }
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const semesterData = {
        ...currentSemester,
        department_id: selectedDepartment.department_id
      };

      if (selectedId) {
        // Update existing semester
        await updateSemester(selectedId, semesterData);
        setSemesters(semesters.map(semester => 
          semester.semester_id === selectedId 
            ? { ...semester, ...semesterData, semester_id: selectedId } 
            : semester
        ));
        
        setSnackbar({
          open: true,
          message: 'Semester updated successfully',
          severity: 'success'
        });
      } else {
        // Create new semester
        const response = await createSemester(semesterData);
        const newSemester = {
          ...semesterData,
          semester_id: response.data.semester_id
        };
        
        setSemesters([...semesters, newSemester]);
        
        setSnackbar({
          open: true,
          message: 'Semester created successfully',
          severity: 'success'
        });
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving semester:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save semester',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <PageHeader 
        title="Semesters Management" 
        subtitle="Add and manage semesters for courses" 
      />
      
      {/* Department Header */}
      {selectedDepartment && (
        <DepartmentHeader returnPath="/semesters" />
      )}

      <Paper sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Semester
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={semesters}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            getRowId={(row) => row.semester_id}
            autoHeight
            disableSelectionOnClick
            sx={{ minHeight: 400 }}
          />
        )}
      </Paper>

      {/* Add/Edit Semester Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedId ? 'Edit Semester' : 'Add Semester'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              name="semester_name"
              label="Semester Name"
              fullWidth
              margin="normal"
              value={currentSemester.semester_name}
              onChange={handleInputChange}
              error={!!formErrors.semester_name}
              helperText={formErrors.semester_name}
            />
            <TextField
              name="academic_year"
              label="Academic Year"
              fullWidth
              margin="normal"
              value={currentSemester.academic_year}
              onChange={handleInputChange}
              error={!!formErrors.academic_year}
              helperText={formErrors.academic_year}
            />
            <TextField
              name="section"
              label="Sections (comma separated, e.g. A,B,C)"
              fullWidth
              margin="normal"
              value={currentSemester.section}
              onChange={handleInputChange}
              placeholder="A,B,C"
            />
            <FormControl fullWidth margin="normal" disabled>
              <InputLabel>Department</InputLabel>
              <Select
                name="department_id"
                value={currentSemester.department_id}
                label="Department"
              >
                <MenuItem value={selectedDepartment?.department_id}>
                  {selectedDepartment?.department_name}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this semester? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Semesters;
